import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Default } from "../../components/default/Default";
import { PostCodesTable } from "../../components/postcodes/Table";
import { McaModal, McaEmpty } from "../../components/mca";
import {
  postCodeDomainList,
  setZipCodeList,
} from "../../reducer/zipCode/ZipCode.action";
import PostRequest from "../../services/PostRequest";
import { showToaster } from "../../common/common";

// A real example postcode / ZIP per Amazon marketplace country, shown as the
// input placeholder once a site is chosen. Keyed by Domain.country_code.
const EXAMPLE_POSTCODES = {
  AU: "2000",       // Sydney
  US: "10001",      // New York
  CA: "M5V 3L9",    // Toronto
  MX: "06000",      // Mexico City
  BR: "01310-100",  // São Paulo
  GB: "SW1A 1AA",   // London
  DE: "10115",      // Berlin
  FR: "75001",      // Paris
  IT: "00184",      // Rome
  ES: "28013",      // Madrid
  PL: "00-001",     // Warsaw
  IE: "D02 AF30",   // Dublin
  TR: "34122",      // Istanbul
  IN: "110001",     // New Delhi
  JP: "100-0001",   // Tokyo
  SG: "018956",     // Singapore
  AE: "Dubai",      // UAE — no postal codes; uses emirate
  SA: "11564",      // Riyadh
  EG: "11511",      // Cairo
  NL: "1012 JS",    // Amsterdam
  SE: "111 29",     // Stockholm
  ZA: "0001",       // Pretoria
};

export const PostCodes = () => {
  const dispatch = useDispatch();
  const zipCodesList = useSelector(
    (state) => state.zipCodeReducer.zipCodesList
  );
  const _domainList = useSelector((state) => state.zipCodeReducer.domainList);

  const [showModal, setShowModal] = useState(false);
  const [getDomainList, setGetDomainList] = useState([]);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [postcode, setPostcode] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleCloseModal = () => {
    setShowModal(false);
    setPostcode("");
  };

  const handleOpenModal = () => {
    setShowModal(true);
  };

  const handleAddPostcode = async (e) => {
    e.preventDefault();

    if (!selectedDomain || !postcode.trim()) {
      showToaster("Please select a domain and enter a postcode.", "error");
      return;
    }

    try {
      setIsSubmitting(true);
      const data = {
        domain: selectedDomain.value,
        zipcode: postcode.trim(),
      };

      await PostRequest("/zipcodes/create-zipcode/", data);
      showToaster("Generating delivery cookie…");
      dispatch(setZipCodeList());
      handleCloseModal();
    } catch (error) {
      showToaster(error.message, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const loadDomainList = () => {
    dispatch(postCodeDomainList());
  };

  useEffect(() => {
    loadDomainList();
    // Fetch the postcode list on mount. The list-fetch previously lived only
    // inside <PostCodesTable>, which is rendered only when zipCodesList is
    // already non-empty — so on a fresh load the list never loaded and the
    // page was stuck on the empty state. Fetch here so the parent's
    // empty-vs-table decision has data.
    dispatch(setZipCodeList());
  }, []);

  useEffect(() => {
    const listDomain = _domainList?.map((domain) => ({
      value: domain.id,
      label: domain.domain,
      countryCode: domain.country_code,
    }));
    setGetDomainList(listDomain || []);
    // No default site — the user must explicitly choose one (see the disabled
    // placeholder <option> in the modal).
  }, [_domainList]);

  return (
    <Default>
      <div className="mca-content">
        {/* Page Head */}
        <div className="pc-head">
          <div>
            <h1 style={{ fontSize: "26px", fontWeight: 700, letterSpacing: "-0.02em" }}>
              Postcodes/ZIP
            </h1>
            <p style={{ color: "var(--mca-muted)", fontSize: "14px", margin: "6px 0 0" }}>
              Delivery postcodes / ZIP codes power location-aware pricing across your requests.
            </p>
          </div>
          <button
            className="mca-btn mca-btn--primary"
            onClick={handleOpenModal}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 5v14M5 12h14" />
            </svg>
            Add Postcode/ZIP
          </button>
        </div>

        {/* Table Section */}
        <div className="mca-table-wrap">
          {zipCodesList && zipCodesList.length > 0 ? (
            <>
              <PostCodesTable />
              <div className="table-foot">
                <span>
                  {zipCodesList.length} postcode{zipCodesList.length !== 1 ? "s" : ""}/ZIP
                </span>
              </div>
            </>
          ) : (
            <McaEmpty
              icon={
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ width: "48px", height: "48px" }}
                >
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                  <polyline points="9 22 9 12 15 12 15 22" />
                </svg>
              }
              title="No postcodes/ZIP yet"
              description="Add your first postcode / ZIP to enable location-aware pricing."
              action={
                <button className="mca-btn mca-btn--primary" onClick={handleOpenModal}>
                  Add Postcode/ZIP
                </button>
              }
              minHeight={true}
            />
          )}
        </div>
      </div>

      {/* Add Postcode Modal */}
      <McaModal
        open={showModal}
        onClose={handleCloseModal}
        title="Add Postcode/ZIP"
        ariaLabelledBy="pcTitle"
        footer={
          <>
            <button
              className="mca-btn mca-btn--outline"
              onClick={handleCloseModal}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              className="mca-btn mca-btn--primary"
              onClick={handleAddPostcode}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Adding..." : "Add Postcode/ZIP"}
            </button>
          </>
        }
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "18px" }}>
          <div className="mca-field">
            <label className="mca-label">
              Domain <span className="req">*</span>
            </label>
            <select
              className="mca-select"
              value={selectedDomain?.value || ""}
              onChange={(e) => {
                // <select> values are always strings, but d.value is domain.id
                // (a number) — a strict === never matched, so the selection
                // reset to undefined. Compare as strings.
                const selected = getDomainList.find(
                  (d) => String(d.value) === e.target.value
                );
                setSelectedDomain(selected || null);
              }}
            >
              <option value="" disabled>
                Select a site…
              </option>
              {getDomainList.map((domain) => (
                <option key={domain.value} value={domain.value}>
                  {domain.label}
                </option>
              ))}
            </select>
          </div>
          <div className="mca-field">
            <label className="mca-label">
              Postcode/ZIP <span className="req">*</span>
            </label>
            <input
              className="mca-input mca-cell-mono"
              placeholder={
                selectedDomain
                  ? EXAMPLE_POSTCODES[selectedDomain.countryCode] ||
                    "Enter postcode / ZIP"
                  : "Select a site first"
              }
              maxLength="12"
              value={postcode}
              disabled={!selectedDomain}
              onChange={(e) => setPostcode(e.target.value)}
            />
            <span className="mca-help">Generating a cookie can take up to a minute.</span>
          </div>
        </div>
      </McaModal>
    </Default>
  );
};
